import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

// The video.js player with its own control bar (Review, the scrub dock's render preview). HD-8: the skin is tokens in
// ui/ui.css (.ui-player, DESIGN rev 2.1 §2.6 Media set); the injected <style> string and the inline styles are gone. The
// video.js options, the control-bar order, the comment markers and the keyboard shortcuts are as they were:
//   ArrowLeft / ArrowRight  one frame back / forward (1/30 s)   |   Space  play / pause
//   (ignored while typing in an input or textarea; listened for in the capture phase, so they win over video.js)
const FRAME = 1 / 30; // ~33ms at 30fps

// Register a "restart from beginning" button once, globally. Sits in the
// control bar in the slot where the volume panel used to be.
const Button = videojs.getComponent('Button');
if (!videojs.getComponent('RestartButton')) {
  class RestartButton extends Button {
    constructor(player, options) {
      super(player, options);
      this.controlText('Restart from beginning');
      // The lucide RotateCcw glyph (§2.7: the one outline set, 16px at a true 1.5px stroke), written as markup because a
      // video.js component builds its own DOM outside React.
      this.el().innerHTML = `
        <span class="vjs-icon-placeholder" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none"
               stroke="currentColor" stroke-width="1.5" vector-effect="non-scaling-stroke"
               stroke-linecap="round" stroke-linejoin="round">
            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
            <path d="M3 3v5h5"></path>
          </svg>
        </span>`;
    }
    handleClick() {
      const p = this.player();
      p.currentTime(0);
      p.play();
    }
  }
  videojs.registerComponent('RestartButton', RestartButton);
}

export default function VideoPlayer({ src, poster, comments = [], onTimeUpdate, seekRef, onCommentDotClick, onPlayerReady }) {
  const containerRef = useRef(null);
  const playerRef    = useRef(null);
  const [duration,       setDuration]       = useState(0);
  const [progressHolder, setProgressHolder] = useState(null);

  useEffect(() => {
    if (!containerRef.current || !src) return;

    const videoEl = containerRef.current.querySelector('video');
    const player  = videojs(videoEl, {
      controls:       true,
      fluid:          false,
      fill:           true,
      playbackRates:  [0.5, 1, 1.25, 1.5, 2],
      sources:        [{ src, type: detectMimeType(src) }],
      html5:          { vhs: { overrideNative: true } },
      preload:           'auto',
      userActions:       { hotkeys: false },
      // Keep the control bar (play, scrubber, time, volume, etc.) visible at
      // all times — default Video.js fades it out after a few seconds of
      // mouse inactivity during playback.
      inactivityTimeout: 0,
      // Explicit children order — replay sits where volume used to be (right
      // after the play button); volume moves to the far right, next to
      // fullscreen. Custom components must be registered before this runs.
      controlBar: {
        children: [
          'playToggle',
          'restartButton',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          'progressControl',
          'remainingTimeDisplay',
          'playbackRateMenuButton',
          'volumePanel',
          'fullscreenToggle',
        ],
      },
      ...(poster ? { poster } : {}),
    });

    playerRef.current = player;

    if (seekRef) {
      seekRef.current = secs => player.currentTime(secs);
    }

    player.ready(() => {
      const holder = player.el().querySelector('.vjs-progress-holder');
      if (holder) {
        holder.style.position = 'relative';
        setProgressHolder(holder);
      }
      onPlayerReady?.();
    });

    player.on('timeupdate', () => onTimeUpdate?.(player.currentTime()));
    player.on('loadedmetadata', () => setDuration(player.duration()));
    // Force first frame to paint so the player isn't black before play
    player.one('loadeddata', () => { if (player.paused()) player.currentTime(0); });

    // Keyboard shortcuts — VJS hotkeys disabled so we handle them here
    function handleKeyDown(e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const p = playerRef.current;
      if (!p || p.isDisposed()) return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        p.currentTime(Math.max(0, p.currentTime() - FRAME));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        p.currentTime(Math.min(p.duration() || 0, p.currentTime() + FRAME));
      } else if (e.key === ' ') {
        e.preventDefault();
        p.paused() ? p.play() : p.pause();
      }
    }
    document.addEventListener('keydown', handleKeyDown, true); // capture phase beats VJS

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      setProgressHolder(null);
      if (seekRef) seekRef.current = null;
      if (playerRef.current && !playerRef.current.isDisposed()) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [src]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="ui-player">
      <div ref={containerRef} data-vjs-player>
        <video className="video-js vjs-big-play-centered" playsInline />
      </div>
      {progressHolder && duration > 0 && createPortal(
        comments.filter(c => !c.parentId).map(c => (
          <CommentDot
            key={c.id}
            left={`${(Number(c.timestampSeconds) / duration) * 100}%`}
            onClick={() => {
              playerRef.current?.currentTime(Number(c.timestampSeconds));
              onCommentDotClick?.(c.id);
            }}
          />
        )),
        progressHolder,
      )}
    </div>
  );
}

// A comment marker on the progress bar (§2.6 Scrub bar: accent dots). left is the comment's position, the one inline value.
function CommentDot({ left, onClick }) {
  return <span className="ui-player__dot" style={{ left }} onClick={onClick} aria-hidden="true" />;
}

function detectMimeType(url) {
  if (/\.webm$/i.test(url)) return 'video/webm';
  if (/\.mov$/i.test(url)) return 'video/quicktime';
  return 'video/mp4';
}
